/* ═══════════════════════════════════════════════════════════════════════════
   HexClean — countries, cities and districts
   ───────────────────────────────────────────────────────────────────────────
   HexClean operates in Saudi Arabia only. This module exists anyway, because
   "one country" and "hard-coded country" are different things: the first is a
   fact about today's coverage, the second is a fact about the code that
   outlives it. Everything that varies by market — currency and its minor unit,
   dial code, phone shape, the `Intl` tags, the payment rails, the service map —
   is a field on `CountryDef` rather than a literal scattered through the app.

   Adding the UAE later is appending a second entry here and translating its
   district names. Nothing else has to move.

   The district `id` is the value that gets stored on an address, sent to the
   API and matched against `Provider.areas`, so it is a stable ASCII key and
   never a translated string — a saved address must not stop matching because
   the user switched to Arabic. `districtLabel()` is the only thing that turns
   it into something a person reads.
   ═══════════════════════════════════════════════════════════════════════════ */

import type { LocalizedText, PaymentMethod } from './types'
import type { Locale } from './format'

export type CountryCode = 'SA'

/** Where an address that was typed rather than searched for is pinned.
 *
 *  The server refuses a pin outside Saudi Arabia — `domain.Address.Validate`
 *  wants lat 16–33, lng 34–56 — so `0,0` is not "no opinion", it is a rejected
 *  address. Street, district and building are what actually locate a door and
 *  they are what a rider reads; this is a starting point, not a claim.
 *
 *  It lives here, exported, because it was two constants in two screens and
 *  they disagreed: the registration form pinned Jeddah and the Addresses screen
 *  pinned Riyadh. That is not a cosmetic difference. Dispatch ranks partners by
 *  distance from the pin, so an address typed on one screen put every Jeddah
 *  partner 843km away and an identical address typed on the other did not —
 *  the same customer, the same street, two different answers to "who is near".
 *
 *  Jeddah, because it is the home market and the city the served district list
 *  starts in, so the coarse pin and the district a form defaults to agree about
 *  which city the customer is in rather than pointing at two. */
export const DEFAULT_PIN = { lat: 21.4858, lng: 39.1925 } as const

export interface CityDef {
  id: string
  name: LocalizedText
  /** Whether HexClean is open here.
   *
   *  A fallback for the value the server owns, not a second source of truth.
   *  It exists so the tables below can say which market is live before
   *  `GET /cities` resolves — without it the bundled fallback would offer
   *  Riyadh and Dammam districts on an address form, which is the exact bug
   *  the served registry was built to stop. */
  active: boolean
}

export interface DistrictDef {
  /** Stable key. Stored, filtered and searched on — never displayed raw. */
  id: string
  name: LocalizedText
  cityId: string
}

export interface CountryDef {
  code: CountryCode
  name: LocalizedText
  dialCode: string
  currency: string
  /** Minor units per major unit. SAR is 100 halalas; KWD would be 1000. */
  minorUnits: number
  /** Digits after the dial code, and the shape they are grouped in. Saudi
   *  mobiles are nine digits and always start with 5. */
  phone: {
    digits: number
    /** Groups to split the national number into, e.g. [2,3,4] → `50 123 4567`. */
    groups: number[]
    placeholder: string
  }
  /** `Intl` tags per locale. Arabic asks for Latin digits explicitly — see the
   *  note in format.ts about why prices do not get Eastern Arabic numerals. */
  numberTag: Record<Locale, string>
  dateTag: Record<Locale, string>
  /** Local payment rail first: in Saudi that is mada, the domestic debit
   *  network, which is what most cards in a wallet here actually are. */
  payments: PaymentMethod[]
  cities: CityDef[]
  districts: DistrictDef[]
}

/* ── Saudi Arabia ─────────────────────────────────────────────────────────── */

const SA_CITIES: CityDef[] = [
  { id: 'jeddah', name: { en: 'Jeddah', ar: 'جدة' }, active: true },
  { id: 'riyadh', name: { en: 'Riyadh', ar: 'الرياض' }, active: false },
  { id: 'dammam', name: { en: 'Dammam & Khobar', ar: 'الدمام والخبر' }, active: false },
]

const SA_DISTRICTS: DistrictDef[] = [
  { id: 'Al Hamra', cityId: 'jeddah', name: { en: 'Al Hamra', ar: 'الحمراء' } },
  { id: 'Al Aziziyah', cityId: 'jeddah', name: { en: 'Al Aziziyah', ar: 'العزيزية' } },
  { id: 'Al Murjan', cityId: 'jeddah', name: { en: 'Al Murjan', ar: 'المرجان' } },
  { id: 'Al Salamah', cityId: 'jeddah', name: { en: 'Al Salamah', ar: 'السلامة' } },
  { id: 'Al Shati', cityId: 'jeddah', name: { en: 'Al Shati', ar: 'الشاطئ' } },
  { id: 'Al Zahra', cityId: 'jeddah', name: { en: 'Al Zahra', ar: 'الزهراء' } },
  { id: 'Al Naeem', cityId: 'jeddah', name: { en: 'Al Naeem', ar: 'النعيم' } },
  { id: 'Al Marwah', cityId: 'jeddah', name: { en: 'Al Marwah', ar: 'المروة' } },
  { id: 'Al Safa', cityId: 'jeddah', name: { en: 'Al Safa', ar: 'الصفا' } },
  { id: 'Al Andalus', cityId: 'jeddah', name: { en: 'Al Andalus', ar: 'الأندلس' } },
  { id: 'Al Khalidiyah', cityId: 'jeddah', name: { en: 'Al Khalidiyah', ar: 'الخالدية' } },
  { id: 'Al Basateen', cityId: 'jeddah', name: { en: 'Al Basateen', ar: 'البساتين' } },
  {
    id: 'Obhur Al Shamaliyah',
    cityId: 'jeddah',
    name: { en: 'Obhur Al Shamaliyah', ar: 'أبحر الشمالية' },
  },
  {
    id: 'Obhur Al Janubiyah',
    cityId: 'jeddah',
    name: { en: 'Obhur Al Janubiyah', ar: 'أبحر الجنوبية' },
  },
  { id: 'Al Nahdah', cityId: 'jeddah', name: { en: 'Al Nahdah', ar: 'النهضة' } },
  { id: 'Al Olaya', cityId: 'riyadh', name: { en: 'Al Olaya', ar: 'العليا' } },
  { id: 'Al Malaz', cityId: 'riyadh', name: { en: 'Al Malaz', ar: 'الملز' } },
  { id: 'Al Nakheel', cityId: 'riyadh', name: { en: 'Al Nakheel', ar: 'النخيل' } },
  { id: 'Al Sahafa', cityId: 'riyadh', name: { en: 'Al Sahafa', ar: 'الصحافة' } },
  { id: 'Hittin', cityId: 'riyadh', name: { en: 'Hittin', ar: 'حطين' } },
  { id: 'Al Yasmin', cityId: 'riyadh', name: { en: 'Al Yasmin', ar: 'الياسمين' } },
  { id: 'Al Narjis', cityId: 'riyadh', name: { en: 'Al Narjis', ar: 'النرجس' } },
  { id: 'Qurtubah', cityId: 'riyadh', name: { en: 'Qurtubah', ar: 'قرطبة' } },
  { id: 'Al Rawdah', cityId: 'riyadh', name: { en: 'Al Rawdah', ar: 'الروضة' } },
  { id: 'Al Wurud', cityId: 'riyadh', name: { en: 'Al Wurud', ar: 'الورود' } },
  { id: 'Al Muruj', cityId: 'riyadh', name: { en: 'Al Muruj', ar: 'المروج' } },
  { id: 'Al Aqiq', cityId: 'riyadh', name: { en: 'Al Aqiq', ar: 'العقيق' } },
  {
    id: 'Diplomatic Quarter',
    cityId: 'riyadh',
    name: { en: 'Diplomatic Quarter', ar: 'الحي الدبلوماسي' },
  },
  { id: 'Irqah', cityId: 'riyadh', name: { en: 'Irqah', ar: 'عرقة' } },
  { id: 'Al Ghadir', cityId: 'riyadh', name: { en: 'Al Ghadir', ar: 'الغدير' } },
  { id: 'Al Faisaliyah', cityId: 'dammam', name: { en: 'Al Faisaliyah', ar: 'الفيصلية' } },
  { id: 'Ar Rakah', cityId: 'dammam', name: { en: 'Ar Rakah', ar: 'الراكة' } },
  { id: 'Al Aqrabiyah', cityId: 'dammam', name: { en: 'Al Aqrabiyah', ar: 'العقربية' } },
]

/* No flag emoji anywhere in this module, deliberately. Windows ships no
   regional-indicator glyphs, so `🇸🇦` degrades to a bare "SA" letter pair on a
   large share of desktop visitors — which reads as a rendering bug rather than
   as a flag. Drawing the Saudi flag as inline SVG is worse again: the shahada
   is sacred text and an approximated one is not a design shortcut worth taking.
   The ISO code rendered as a deliberate badge is honest, identical everywhere,
   and reads as intentional. */
export const SAUDI_ARABIA: CountryDef = {
  code: 'SA',
  name: { en: 'Saudi Arabia', ar: 'المملكة العربية السعودية' },
  dialCode: '+966',
  currency: 'SAR',
  minorUnits: 100,
  phone: { digits: 9, groups: [2, 3, 4], placeholder: '5X XXX XXXX' },
  numberTag: { en: 'en-SA', ar: 'ar-SA-u-nu-latn' },
  dateTag: { en: 'en-GB', ar: 'ar-SA-u-nu-latn-ca-gregory' },
  payments: ['mada', 'card', 'apple-pay', 'cash', 'wallet'],
  cities: SA_CITIES,
  districts: SA_DISTRICTS,
}

/* ── registry ─────────────────────────────────────────────────────────────── */

export const COUNTRIES: CountryDef[] = [SAUDI_ARABIA]

export const DEFAULT_COUNTRY: CountryCode = 'SA'

export function getCountry(code: CountryCode = DEFAULT_COUNTRY): CountryDef {
  return COUNTRIES.find((c) => c.code === code) ?? SAUDI_ARABIA
}

/** Whether the picker is worth showing as a choice rather than a statement.
 *  With one market it renders as a "we're here, more coming" row instead of a
 *  select the user can get wrong. */
export function hasMultipleCountries(): boolean {
  return COUNTRIES.length > 1
}

/* ── districts ────────────────────────────────────────────────────────────── */

const DISTRICT_BY_ID = new Map(SA_DISTRICTS.map((d) => [d.id, d]))
const CITY_BY_ID = new Map(SA_CITIES.map((c) => [c.id, c]))

/** Stable keys of the districts in the markets that are open, in display order.
 *  This is what `api.getAreas()` serves **in mock mode**, where the seed is the
 *  server. Against the real API the list comes off the wire — so nothing may
 *  treat this as the set of areas that exist. See `districtsByCity`.
 *
 *  Filtered by the city's `active`, because that is what the server's `/areas`
 *  applies and a fallback that disagreed would put a district from an unopened
 *  market on an address form. */
export const DISTRICT_IDS: string[] = SA_DISTRICTS.filter(
  (d) => SA_CITIES.find((c) => c.id === d.cityId)?.active,
).map((d) => d.id)

/** The markets the bundled table says are open. The mock's `getCities`. */
export const OPEN_CITIES: CityDef[] = SA_CITIES.filter((c) => c.active)

export const DEFAULT_DISTRICT = 'Al Hamra'

/** Names for served areas, as the server described them.
 *
 *  A module-level registry rather than a value threaded through every caller,
 *  and that is a deliberate trade. `districtLabel` is reached from `addressLine`
 *  in format.ts, which is a pure formatter called from half a dozen screens;
 *  threading a lookup into it would change the signature of the most-used
 *  function in the app to fix a labelling bug. The registry is written once,
 *  from the areas query, and only ever read after that.
 *
 *  What it is not: a second source of truth about which areas *exist*. Nothing
 *  reads this to decide what to offer — the query's own data does that. This
 *  only answers "what is this district called", for ids the static table below
 *  has never heard of. */
const SERVED_NAMES = new Map<string, LocalizedText>()

/** Records the names the server sent. Called from the areas query.
 *
 *  Additive: an id already known keeps the newer name, and nothing is removed.
 *  An area withdrawn from service still needs a label, because addresses saved
 *  against it are still in people's accounts and a receipt for last week's order
 *  has to render. */
export function registerAreaNames(
  areas: readonly { id: string; name: LocalizedText; cityId?: string }[],
): void {
  for (const area of areas) {
    SERVED_NAMES.set(area.id, area.name)
    /* The city travels with the area now. Grouping used to be this file's job
       — a district was filed under whichever city the table below named — so a
       market the panel opened this afternoon had no heading and its districts
       fell through to the ungrouped branch. The server owns which market a
       district is in, and this is where that answer lands.

       Guarded on truthiness so a server that has not sent one yet leaves the
       bundled answer in place rather than blanking it. */
    if (area.cityId) SERVED_CITY_OF.set(area.id, area.cityId)
  }
}

/** The markets the server says it is open in, and their order. Written once,
 *  from the cities query.
 *
 *  Replaced wholesale rather than merged, unlike the names above: a market that
 *  has closed must stop being a heading and stop being named in "we serve …",
 *  which is the whole point of the operator being able to close one. Labels are
 *  additive because a closed market's districts still have to render on the
 *  addresses people saved; headings are not, because there is nothing to head. */
const SERVED_CITY_OF = new Map<string, string>()
let SERVED_CITIES: CityDef[] = []

export function registerCities(cities: readonly { id: string; name: LocalizedText }[]): void {
  SERVED_CITIES = cities.map((c) => ({ id: c.id, name: c.name, active: true }))
}

/** The markets to name and to group under. Falls back to the bundled table's
 *  open entries before the first load resolves. */
export function openCities(): CityDef[] {
  return SERVED_CITIES.length > 0 ? SERVED_CITIES : OPEN_CITIES
}

/** `'Al Olaya'` → `العليا` in Arabic.
 *
 *  Three sources, in order of authority. The server's name first: it owns which
 *  areas exist, and against the real API ten of the fifteen it serves have no
 *  row in the table below — those rendered as their own Latin keys in the middle
 *  of an Arabic form, which is what this ordering fixes. Then the static table,
 *  which is what mock mode has and what the grouping is built from. Then the key
 *  itself, so an unknown area is words rather than blank. */
export function districtLabel(id: string, locale: Locale = 'en'): string {
  const served = SERVED_NAMES.get(id)
  if (served) return (locale === 'ar' ? served.ar : served.en) || served.en || id
  const d = DISTRICT_BY_ID.get(id)
  if (!d) return id
  return (locale === 'ar' ? d.name.ar : d.name.en) || d.name.en
}

/** Both scripts at once, for the places that need a `LocalizedText` rather than
 *  a rendered string — the mock's `getAreas`, which serves the shape the API
 *  serves. Falls back to the key in both scripts, which is what an unknown area
 *  is worth saying in either. */
export function districtName(id: string): LocalizedText {
  const served = SERVED_NAMES.get(id)
  if (served) return served
  const d = DISTRICT_BY_ID.get(id)
  return d ? d.name : { en: id, ar: id }
}

/** The city id a district belongs to, or null for a key we do not recognise.
 *
 *  This is the whole of the geometry HexClean has. There are no coordinates on
 *  a district — inventing them to compute a distance would be fake precision,
 *  and a partner's coverage is a list of districts anyway, not a radius. So
 *  "near" means one of two things and the matcher says which: the same
 *  district, or a different district of the same city. A Jeddah crew is not
 *  "far" from a Riyadh address, it is unavailable, and returning null here is
 *  what lets the matcher say so rather than ranking it last. */
export function cityIdOfDistrict(id: string): string | null {
  return SERVED_CITY_OF.get(id) ?? DISTRICT_BY_ID.get(id)?.cityId ?? null
}

/** The city a district sits in, for the "Al Hamra · Jeddah" second line. */
export function cityLabelForDistrict(id: string, locale: Locale = 'en'): string {
  const cityId = cityIdOfDistrict(id)
  if (!cityId) return ''
  return cityLabel(cityId, locale)
}

export function cityLabel(cityId: string, locale: Locale = 'en'): string {
  const c = SERVED_CITIES.find((x) => x.id === cityId) ?? CITY_BY_ID.get(cityId)
  if (!c) return cityId
  return (locale === 'ar' ? c.name.ar : c.name.en) || c.name.en
}

/** Districts grouped by city, for a picker that lists "Riyadh" as a heading
 *  rather than presenting fifteen flat rows the user has to scan.
 *
 *  **This must render every id it is given.** It used to filter `SA_DISTRICTS`
 *  by the ids instead, which quietly meant the table above was the gatekeeper:
 *  an area the server serves and this file has never heard of simply vanished
 *  from the picker. Against the real API that was ten of fifteen Riyadh
 *  districts gone and, because the district select is required, **an address
 *  form that could not be submitted at all**.
 *
 *  The server owns which areas exist; this file owns how they are labelled and
 *  grouped. So an id with no row here still gets a row in the list, under no
 *  city heading — we genuinely do not know its city, and guessing one is how a
 *  Jeddah district ends up filed under Riyadh the day the API serves two. Its
 *  label falls back to the key, which `districtLabel` already does. */
export function districtsByCity(
  ids: readonly string[] = DISTRICT_IDS,
): { city: CityDef | null; districts: DistrictDef[] }[] {
  const wanted = [...new Set(ids)]

  /* Headings come from the cities query, plus any city id an area mentions
     that the query has not named — a district whose market this app has never
     heard of still gets a heading of its own rather than being filed under
     somebody else's. */
  const headings = [...openCities()]
  for (const id of wanted) {
    const cityId = cityIdOfDistrict(id)
    if (cityId && !headings.some((c) => c.id === cityId)) {
      headings.push({ id: cityId, name: { en: cityId, ar: cityId }, active: true })
    }
  }

  const grouped: { city: CityDef | null; districts: DistrictDef[] }[] = headings.map((city) => ({
    city,
    districts: wanted
      .filter((id) => cityIdOfDistrict(id) === city.id)
      .map(
        (id): DistrictDef =>
          DISTRICT_BY_ID.get(id) ?? { id, cityId: city.id, name: districtName(id) },
      ),
  }))

  /* Server order, not this file's, for a district with no city at all — that is
     the only ordering information available about it, and dropping it is how an
     address form ends up unsubmittable. */
  const unplaced = wanted
    .filter((id) => !cityIdOfDistrict(id))
    .map((id): DistrictDef => ({ id, cityId: '', name: districtName(id) }))
  if (unplaced.length > 0) grouped.push({ city: null, districts: unplaced })

  return grouped.filter((g) => g.districts.length > 0)
}

/** Match a district against a search box in either language, so typing
 *  "العليا" and typing "olaya" both find the same row. */
export function districtMatches(id: string, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const d = DISTRICT_BY_ID.get(id)
  if (!d) return id.toLowerCase().includes(q)
  const city = CITY_BY_ID.get(d.cityId)
  const hay = [d.id, d.name.en, d.name.ar, city?.name.en, city?.name.ar]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return hay.includes(q)
}
